import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { getMonthlySpending } from '../../utils/monthlyAnalytics';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import ProgressDashes from '../../components/ProgressDashes';
import { StoryNavigation } from '../../components/StoryNavigation';
import { useStoryNavigation } from '../../hooks/useStoryNavigation';

const MonthlySpendingPage = () => {
  const navigate = useNavigate();
  const transactions = useSelector((state: RootState) => state.transactions.items);
  const monthlySpending = useMemo(() => getMonthlySpending(transactions), [transactions]);
  const { goToNext, goToPrev, hasNext, hasPrev } = useStoryNavigation();

  // Auto-navigate to the next page after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/insights/ride-or-die');
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-[#221A51]/[0.07] relative">
      <StoryNavigation
        onNext={goToNext}
        onPrev={goToPrev}
        hasNext={hasNext}
        hasPrev={hasPrev}
      />
      <div className="px-4 sm:px-16 pt-16 sm:pt-20 max-w-4xl mx-auto">
        {/* Progress dashes */}
        <ProgressDashes currentStep={3} totalSteps={11} />

        <div className="px-2 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-1 sm:space-y-2"
          >
            <h1 className="text-2xl sm:text-[34px] leading-[140%] font-black text-[#221A51]">
              Monthly Spending
              <br />
              Patterns
            </h1>
            <p className="text-sm sm:text-base text-[#221A51]/60 font-medium">
              Track your spending trends throughout the year.
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 sm:mt-12 relative"
          >
            {/* Shadow box */}
            <div className="absolute top-[4px] left-[4px] w-full h-full bg-black rounded-xl" />
            
            {/* Main content box */}
            <div className="bg-white rounded-xl p-4 sm:p-6 relative border-2 border-black">
              <div className="space-y-6 sm:space-y-8">
                {monthlySpending.map((month, index) => (
                  <motion.div
                    key={month.month}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="space-y-1.5 sm:space-y-2"
                  >
                    <div className="flex justify-between">
                      <div className="font-medium text-[#221A51] text-base sm:text-lg">
                        {month.month}
                      </div>
                      <div className="font-semibold text-[#221A51] text-base sm:text-lg">
                        ${month.amount.toLocaleString()}
                      </div>
                    </div>

                    <div className="h-3 sm:h-4 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(month.amount / month.maxAmount) * 100}%` }}
                        transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                        className="h-full bg-[#9B86EA] rounded-full"
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Decorative gear */}
        <motion.div
          initial={{ rotate: -30, scale: 0.8, opacity: 0 }}
          animate={{ rotate: 0, scale: 1, opacity: 1 }}
          transition={{ delay: 0.8, type: "spring" }}
          className="absolute -bottom-20 -right-20 w-64 h-64"
        >
          <div className="w-full h-full rounded-full bg-[#9B86EA]/20 relative overflow-hidden"
            style={{
              maskImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M100 0 L100 40 L160 40 L160 0 Z' fill='%23000'/%3E%3Cpath d='M100 160 L100 200 L160 200 L160 160 Z' fill='%23000'/%3E%3Cpath d='M0 100 L40 100 L40 160 L0 160 Z' fill='%23000'/%3E%3Cpath d='M160 100 L200 100 L200 160 L160 160 Z' fill='%23000'/%3E%3C/svg%3E")`,
              WebkitMaskImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M100 0 L100 40 L160 40 L160 0 Z' fill='%23000'/%3E%3Cpath d='M100 160 L100 200 L160 200 L160 160 Z' fill='%23000'/%3E%3Cpath d='M0 100 L40 100 L40 160 L0 160 Z' fill='%23000'/%3E%3Cpath d='M160 100 L200 100 L200 160 L160 160 Z' fill='%23000'/%3E%3C/svg%3E")`
            }}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default MonthlySpendingPage;
